// SC: Bridges third-party cart events to the selected native drawer without changing UpCart behavior.
(() => {
  if (window.__scCartIntegrationsInitialized) return;

  window.__scCartIntegrationsInitialized = true;

  let flitsCreditState = null;
  let flitsRestoreId = 0;
  let giftLabBindTimer = null;
  let giftLabBindStartedAt = 0;
  let giftLabRetryDelay = 75;
  const handledEvents = new WeakSet();

  const isNative = () =>
    typeof window.scCartExperiment?.scIsNative === 'function'
      ? window.scCartExperiment.scIsNative()
      : window.scCartVariant === 'native';

  const isEnabled = () => window.scCartIntegrationsDisabled !== true;

  const synchronize = (source, options = {}) => {
    if (!isEnabled() || !isNative() || !document.querySelector('sc-cart-drawer')) return;

    const handler = window.scCartExperiment?.scHandleCartMutation;

    if (typeof handler === 'function') {
      Promise.resolve(handler({ source, ...options })).catch((error) => {
        console.warn(`[SC cart integrations] ${source} could not be synchronized.`, error);
      });
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:changed', { detail: { source } }));
  };

  const listen = (targets, eventName, handler) => {
    targets.forEach((target) => target.addEventListener(eventName, handler));
  };

  const isRepeatedEvent = (event) => {
    if (handledEvents.has(event)) return true;

    handledEvents.add(event);
    return false;
  };

  const isCartMutationRequest = (settings) => {
    const requestUrl = String(settings?.url || '');
    const requestMethod = String(settings?.type || settings?.method || 'GET').toUpperCase();

    return (
      requestMethod !== 'GET' &&
      /\/cart\/(?:add|change|update|clear)(?:\.js|\.json)?(?:[?#]|$)/.test(requestUrl)
    );
  };

  const bindGiftLabAjax = () => {
    window.clearTimeout(giftLabBindTimer);
    giftLabBindTimer = null;

    const giftLabJquery = window.glpro?.$;

    if (typeof giftLabJquery?.fn?.on !== 'function') {
      if (!giftLabBindStartedAt) giftLabBindStartedAt = Date.now();
      if (Date.now() - giftLabBindStartedAt >= 30000) return;

      giftLabBindTimer = window.setTimeout(bindGiftLabAjax, giftLabRetryDelay);
      giftLabRetryDelay = Math.min(Math.ceil(giftLabRetryDelay * 1.5), 1000);
      return;
    }

    giftLabJquery(document)
      .off('ajaxComplete.scCartExperimentGiftLab')
      .on('ajaxComplete.scCartExperimentGiftLab', (_event, xhr, settings) => {
        if (!isCartMutationRequest(settings)) return;
        if (Number(xhr?.status) < 200 || Number(xhr?.status) >= 300) return;

        // SC: GiftLab changes gift wrap/cards/add-ons through its private jQuery
        // instance and does not emit a storefront cart event after completion.
        synchronize('giftlab-ajax', {
          ignoreDuringSync: false,
          delay: 80,
        });
      });
  };

  const onGloboProductAdded = (event) => {
    if (isRepeatedEvent(event)) return;
    synchronize('globo-related-products', { open: true, delay: 140 });
  };

  const onObservedCartMutation = (event) => {
    if (isRepeatedEvent(event)) return;
    synchronize(event.type, {
      open: event.detail?.open === true,
      ignoreDuringSync: true,
      delay: 180,
    });
  };

  const onUpcartObservedCartMutation = (event) => {
    if (isRepeatedEvent(event)) return;
    synchronize(event.type, {
      open: event.detail?.open === true,
      ignoreDuringSync: true,
      // SC: UpCart can emit its first notification before its cached cart is current.
      delay: 650,
    });
  };

  const onProductAdded = (event) => {
    if (isRepeatedEvent(event)) return;
    synchronize(event.type, {
      open: true,
      ignoreDuringSync: true,
      delay: 180,
    });
  };

  listen(
    [document, window],
    'globoRelatedProductsAddedProductsToCart',
    onGloboProductAdded
  );
  listen([document, window], 'globoRelatedProductsUpdateCart', onGloboProductAdded);

  listen(
    [document, window],
    'gbbExt-shopify-cart-changed',
    onUpcartObservedCartMutation
  );

  [
    'cart:refresh',
    'cart:updated',
    'cart:build',
    'cart:update',
    'dispatch:cart-drawer:refresh',
    'removedFromCart',
    'theme:cart:reload',
    'theme:cart:refresh',
    'theme:cartchanged',
    'update_cart',
    'Flits:AjaxCart:CartCleared',
    'Flits:AjaxCart:CartUpdated',
  ].forEach((eventName) => listen([document, window], eventName, onObservedCartMutation));

  ['product:added', 'productAddedToCart', 'Flits:AjaxCart:ProductAdded'].forEach((eventName) =>
    listen([document, window], eventName, onProductAdded)
  );

  const captureFlitsCreditState = (event) => {
    if (!isEnabled() || !isNative()) return;

    const input = event.detail?.drawer?.querySelector('.flits-want-to-use-credit');

    if (!input) {
      flitsCreditState = null;
      return;
    }

    flitsCreditState = {
      tagName: input.tagName,
      type: input.type,
      value: input.value,
      checked: Boolean(input.checked),
    };
  };

  const restoreFlitsCreditState = (drawer, restoreId, attempt = 0) => {
    if (restoreId !== flitsRestoreId || !flitsCreditState || !drawer?.isConnected) return;

    const input = drawer.querySelector('.flits-want-to-use-credit');

    const hasSelectedOption =
      input?.tagName !== 'SELECT' ||
      Array.from(input.options).some((option) => option.value === flitsCreditState.value);

    if (!input || !hasSelectedOption) {
      if (attempt < 12) {
        window.setTimeout(() => restoreFlitsCreditState(drawer, restoreId, attempt + 1), 250);
      }
      return;
    }

    input.value = flitsCreditState.value;
    if (input.type === 'checkbox' || input.type === 'radio') input.checked = flitsCreditState.checked;
  };

  const reinitializeFlits = (event) => {
    if (!isEnabled() || !isNative()) return;
    if (typeof window.Flits?.checkAndSetupStoreCreditDiv !== 'function') return;

    const drawer = event.detail?.drawer;
    const restoreId = ++flitsRestoreId;

    window.setTimeout(() => {
      if (restoreId !== flitsRestoreId || !isNative()) return;

      try {
        window.Flits.checkAndSetupStoreCreditDiv();
        restoreFlitsCreditState(drawer, restoreId);
      } catch (error) {
        console.warn('[SC cart integrations] Flits store credit could not be restored.', error);
      }
    });
  };

  document.addEventListener('sc:cart-before-render', captureFlitsCreditState);
  document.addEventListener('sc:cart-rendered', reinitializeFlits);

  bindGiftLabAjax();
})();
